/**
 * Shared UI primitives + tokens for the Support area, matching the approved
 * mockup's design system (teal brand, status badges, severity dots, category
 * tags, person avatars). Kept in one place so the list / new / detail pages
 * stay visually consistent.
 */
import Box from "@mui/material/Box";

import { getUserFullNameFromEmail } from "../../../utils/functions/getUserFullNameFromEmail";

// ── Design tokens (from the mockup :root) ────────────────────────────────────
export const T = {
  teal600: "#1798b4",
  teal500: "#21a9c4",
  teal50: "#e9f7fa",
  teal100: "#d4eff5",
  ink900: "#16242b",
  ink700: "#33454e",
  ink500: "#5d7079",
  ink300: "#9aaab1",
  line: "#e3e9ec",
  bg: "#f6f9fa",
  card: "#ffffff",
  amber: "#d97706",
  orange: "#ea580c",
  red: "#dc2626",
  green: "#16a34a",
  slate: "#64748b",
  blue: "#2563eb",
  radius: "10px",
  shadow: "0 1px 2px rgba(22,36,43,.05), 0 4px 16px rgba(22,36,43,.05)",
};

export const STATUS_META = {
  Open: { bg: "#e9f7fa", fg: "#1798b4", dot: "#21a9c4" },
  "In Progress": { bg: "#eff4ff", fg: "#2563eb", dot: "#2563eb" },
  Resolved: { bg: "#effaf2", fg: "#16a34a", dot: "#16a34a" },
  Closed: { bg: "#f1f4f6", fg: "#64748b", dot: "#64748b" },
};

export const SEVERITY_META = {
  Low: { color: "#64748b", pillBg: "#f1f4f6" },
  Medium: { color: "#d97706", pillBg: "#fef3e2" },
  High: { color: "#ea580c", pillBg: "#fdecdf" },
  Critical: { color: "#dc2626", pillBg: "#fdecec" },
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#1798b4",
  "#7c6bd1",
  "#d98232",
  "#2563eb",
  "#16a34a",
  "#0891b2",
  "#9333ea",
  "#0d9488",
];

const hashStr = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

export const avatarColor = (key = "") =>
  AVATAR_COLORS[hashStr(String(key)) % AVATAR_COLORS.length];

export const initialsFromName = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Display label for user dropdowns: "{fullName} <email>" when a name is known,
// otherwise just the email.
export const formatUserOption = (email, users) => {
  if (!email) return "";
  const name = getUserFullNameFromEmail(users, email);
  return name && name !== email ? `${name} <${email}>` : email;
};

// Resolve an email to { name, initials, color, isYou } using the users slice.
export const resolvePerson = (email, users, myEmail) => {
  if (!email) return null;
  const name = getUserFullNameFromEmail(users, email) || email;
  const isYou = myEmail && email.toLowerCase() === myEmail.toLowerCase();
  return {
    email,
    name,
    display: isYou ? "You" : name,
    initials: initialsFromName(name === email ? email : name),
    color: avatarColor(email),
    isYou,
  };
};

// ── Components ────────────────────────────────────────────────────────────────
export const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.Closed;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "12px",
        fontWeight: 600,
        borderRadius: "999px",
        px: "11px",
        py: "4px",
        whiteSpace: "nowrap",
        background: m.bg,
        color: m.fg,
      }}
    >
      <Box
        component="span"
        sx={{ width: 6, height: 6, borderRadius: "50%", background: m.dot }}
      />
      {status}
    </Box>
  );
};

// variant: "dot" (table) or "pill" (detail header)
export const SeverityIndicator = ({ severity, variant = "dot" }) => {
  const m = SEVERITY_META[severity] || SEVERITY_META.Low;
  if (variant === "pill") {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "7px",
          fontSize: "12.5px",
          fontWeight: 700,
          color: m.color,
          background: m.pillBg,
          borderRadius: "999px",
          px: "11px",
          py: "4px",
        }}
      >
        <Box
          component="span"
          sx={{ width: 7, height: 7, borderRadius: "50%", background: m.color }}
        />
        {severity}
      </Box>
    );
  }
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "7px",
        fontSize: "12.5px",
        fontWeight: 600,
        color: m.color,
      }}
    >
      <Box
        component="span"
        sx={{ width: 8, height: 8, borderRadius: "50%", background: m.color }}
      />
      {severity}
    </Box>
  );
};

export const CategoryTag = ({ category }) =>
  category ? (
    <Box
      component="span"
      sx={{
        fontSize: "11.5px",
        fontWeight: 600,
        color: T.ink700,
        background: "#f1f4f6",
        borderRadius: "6px",
        px: "8px",
        py: "3px",
        whiteSpace: "nowrap",
      }}
    >
      {category}
    </Box>
  ) : (
    "—"
  );

export const UserAvatar = ({ email, name, size = 26 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      fontSize: size <= 26 ? "10.5px" : "12px",
      fontWeight: 700,
      color: "#fff",
      flexShrink: 0,
      background: avatarColor(email || name),
    }}
  >
    {initialsFromName(name || email)}
  </Box>
);

// Person cell — avatar + name, or italic "Unassigned" when empty.
export const PersonCell = ({ email, users, myEmail }) => {
  const p = resolvePerson(email, users, myEmail);
  if (!p)
    return (
      <Box
        component="span"
        sx={{ color: T.ink300, fontStyle: "italic", fontSize: "13px" }}
      >
        Unassigned
      </Box>
    );
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "9px", whiteSpace: "nowrap" }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          fontSize: "10.5px",
          fontWeight: 700,
          color: "#fff",
          background: p.color,
          flexShrink: 0,
        }}
      >
        {p.initials}
      </Box>
      {p.display}
    </Box>
  );
};

// App-authored HTML (from HtmlEditor) rendered read-only. Supports inline images.
export const HtmlBlock = ({ html, sx }) =>
  html ? (
    <Box
      sx={{
        fontSize: "14px",
        lineHeight: 1.65,
        color: T.ink700,
        "& p": { mb: "10px" },
        "& ul, & ol": { m: "0 0 10px 20px" },
        "& img": { maxWidth: "100%", borderRadius: "6px", my: 1 },
        "& code": {
          background: "#f1f4f6",
          borderRadius: "5px",
          px: "6px",
          py: "2px",
          fontSize: "12.5px",
        },
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  ) : (
    <Box sx={{ fontSize: "14px", color: T.ink300 }}>—</Box>
  );
